import React from 'react';
import type { LayoutItem, WidgetJSON, TableRowItem, TableCellItem, NumberLineItem, VerticalOperationItem, VopRowItem, VopCarryRowItem, VopSeparatorItem, VopCellItem, ClockItem } from '../../transformers/zod-schemas';
import { WidgetRenderer } from '../widgets';
import { NumberLineRenderer } from './layouts/number-line/NumberLineRenderer';
import { ClockRenderer } from './layouts/clock/ClockRenderer';
import { JSON_TYPES, STATEFUL_WIDGET_TYPES } from '../../constants';
import { isArabic } from '../utils';
import type { UserAnswerValue } from '../types';
import type { ValidationStatus } from '../widgets/types';
import type { WidgetValidation } from '../../validators';

const toValidationStatus = (v: boolean | undefined): ValidationStatus =>
  v === true ? 'correct' : v === false ? 'incorrect' : null;

export interface LayoutRendererProps {
  item: LayoutItem;
  widgets: Record<string, WidgetJSON>;
  values: Record<string, UserAnswerValue>;
  droppedInputs: Set<string>;
  validationDetails?: Record<string, WidgetValidation> | null;
  onChange: (widgetId: string, value: UserAnswerValue, fromDrop?: boolean, sourceOptionId?: string) => void;
  onInputFocus?: (widgetId: string) => void;
  /** Incremented on reset. Drives remount of stateful items: NumberLine and MatcherWidget. */
  resetKey?: number;
}

/**
 * DUMB layout renderer - no opinions about spacing or layout.
 */
export const LayoutRenderer: React.FC<LayoutRendererProps> = ({
  item,
  widgets,
  values,
  droppedInputs,
  validationDetails,
  onChange,
  onInputFocus,
  resetKey = 0,
}) => {
  switch (item.type) {
    case JSON_TYPES.QUESTION_TEXT: {
      const hasArabic = isArabic(item.value);
      return (
        <span className={`question-text ${hasArabic ? 'question-text-arabic' : ''}`}>
          {item.value}
        </span>
      );
    }

    case JSON_TYPES.TEXT: {
      const isNum = /^[\d.,\s]+$/.test(item.value);
      return (
        <span className={`text-item ${isNum ? 'text-item-number' : ''} ${isArabic(item.value) ? 'text-item-arabic' : 'text-item-not-arabic'}`}>
          {item.value}
        </span>
      );
    }

    case JSON_TYPES.HIGHLIGHTED_TEXT: {
      const hasArabic = isArabic(item.value);
      // For non-Arabic content (numbers, Latin), explicitly set LTR to prevent reversal in RTL context
      const dirAttr = !hasArabic ? 'ltr' : undefined;
      if (item.highlight) {
        const [start, end] = item.highlight;
        return (
          <span className="highlighted-text text-item" dir={dirAttr}>
            {item.value.slice(0, start)}
            <mark className="highlight text-item">{item.value.slice(start, end)}</mark>
            {item.value.slice(end)}
          </span>
        );
      }
      return (
        <mark className="highlighted-text text-item" dir={dirAttr}>
          {item.value}
        </mark>
      );
    }

    case JSON_TYPES.COLOR_TEXT: {
      const hasArabic = isArabic(item.value);
      const dirAttr = !hasArabic ? 'ltr' : undefined;
      if (item.highlight) {
        const [start, end] = item.highlight;
        return (
          <span className="color-text text-item" dir={dirAttr}>
            {item.value.slice(0, start)}
            <span className="color-text-colored" style={{ color: item.color }}>{item.value.slice(start, end)}</span>
            {item.value.slice(end)}
          </span>
        );
      }
      return (
        <span className="color-text text-item" style={{ color: item.color }} dir={dirAttr}>
          {item.value}
        </span>
      );
    }

    case JSON_TYPES.BOLD_TEXT: {
      const hasArabic = isArabic(item.value);
      const dirAttr = !hasArabic ? 'ltr' : undefined;
      if (item.highlight) {
        const [start, end] = item.highlight;
        return (
          <span className="bold-text text-item" dir={dirAttr}>
            {item.value.slice(0, start)}
            <strong className="bold-text-strong text-item">{item.value.slice(start, end)}</strong>
            {item.value.slice(end)}
          </span>
        );
      }
      return (
        <strong className="bold-text text-item" dir={dirAttr}>
          {item.value}
        </strong>
      );
    }

    case JSON_TYPES.IMAGE:
      return (
        <img
          className="image-item text-item"
          src={item.src}
          alt={item.alt}
          {...(item.width !== undefined && { width: item.width })}
          {...(item.height !== undefined && { height: item.height })}
          loading="lazy"
        />
      );

    case JSON_TYPES.WIDGET_REF: {
      const widget = widgets[item.widgetId];
      if (!widget) return null;
      const validationDetail = validationDetails?.[item.widgetId];
      const isCorrect = validationDetail?.isCorrect;
      const validationStatus = isCorrect === true ? 'correct' : isCorrect === false ? 'incorrect' : null;
      const widgetKey = STATEFUL_WIDGET_TYPES.has(widget.type)
        ? `${item.widgetId}-${resetKey}`
        : item.widgetId;
      return (
        <WidgetRenderer
          key={widgetKey}
          widget={widget}
          widgetId={item.widgetId}
          value={values[item.widgetId] || ''}
          values={values as Record<string, string>}
          isDropped={droppedInputs.has(item.widgetId)}
          validationStatus={validationStatus}
          numValidationStatus={toValidationStatus(validationDetails?.[`${item.widgetId}__num`]?.isCorrect)}
          denValidationStatus={toValidationStatus(validationDetails?.[`${item.widgetId}__den`]?.isCorrect)}
          validationDetail={validationDetail}
          droppedInputs={droppedInputs}
          widgets={widgets}
          onChange={onChange}
          onInputFocus={onInputFocus}
        />
      );
    }

    case JSON_TYPES.SPACE:
      return <span className="space">&nbsp;</span>;

    case JSON_TYPES.LINEBREAK:
      return null;

    case JSON_TYPES.OPERATOR:
      return <span className="operator">{item.operator}</span>;

    case JSON_TYPES.GROUP:
      return (
        <span className="group">
          {item.items.map((subItem: LayoutItem) => (
            <LayoutRenderer
              key={subItem.id}
              item={subItem}
              widgets={widgets}
              values={values}
              droppedInputs={droppedInputs}
              validationDetails={validationDetails}
              onChange={onChange}
              onInputFocus={onInputFocus}
              resetKey={resetKey}
            />
          ))}
        </span>
      );

    case JSON_TYPES.TABLE:
      return (
        <div className="exercise-table-container">
          <table className="exercise-table">
            <tbody>
              {item.rows.map((row: TableRowItem) => (
                <tr key={row.id} className="exercise-table-row">
                  {row.cells.map((cell: TableCellItem) => (
                    <td
                      key={cell.id}
                      className="exercise-table-cell"
                      colSpan={cell.colspan || 1}
                    >
                      {cell.items.map((subItem: LayoutItem) => (
                        <LayoutRenderer
                          key={subItem.id}
                          item={subItem}
                          widgets={widgets}
                          values={values}
                          droppedInputs={droppedInputs}
                          validationDetails={validationDetails}
                          onChange={onChange}
                          onInputFocus={onInputFocus}
                          resetKey={resetKey}
                        />
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case JSON_TYPES.NUMBER_LINE:
      return (
        <NumberLineRenderer
          key={resetKey}
          item={item as NumberLineItem}
          widgets={widgets}
          values={values}
          droppedInputs={droppedInputs}
          validationDetails={validationDetails}
          onChange={onChange}
          onInputFocus={onInputFocus}
        />
      );

    case JSON_TYPES.VERTICAL_OPERATION: {
      const vop = item as VerticalOperationItem;
      // If any row carries its own op, use per-row ops exclusively.
      // Otherwise fall back to block op on the last operand row (backwards compat).
      const anyRowHasOp = vop.items.some(
        (x) => x.type === JSON_TYPES.VOP_ROW && (x as VopRowItem).op !== undefined
      );
      let operatorRowIdx = -1;
      if (!anyRowHasOp && vop.op !== undefined) {
        const firstSepIdx = vop.items.findIndex((x) => x.type === JSON_TYPES.VOP_SEPARATOR);
        for (let i = (firstSepIdx === -1 ? vop.items.length : firstSepIdx) - 1; i >= 0; i--) {
          const curr = vop.items[i];
          if (curr && curr.type === JSON_TYPES.VOP_ROW) {
            operatorRowIdx = i;
            break;
          }
        }
      }

      const totalCols = vop.cols + 1; // reserve first column for operator

      return (
        <div
          className="vop"
          style={{ '--vop-cols': totalCols } as React.CSSProperties}
          dir="ltr"
        >
          {vop.items.map((child, idx) => {
            if (child.type === JSON_TYPES.VOP_SEPARATOR) {
              const sep = child as VopSeparatorItem;
              return <div key={sep.id} className="vop-sep" />;
            }
            if (child.type === JSON_TYPES.VOP_CARRY_ROW) {
              const carry = child as VopCarryRowItem;
              const carryLeadingEmpty = Math.max(0, vop.cols - carry.cells.length);
              return (
                <div key={carry.id} className="vop-row vop-carry-row">
                  <span className="vop-op-cell" />
                  {Array.from({ length: carryLeadingEmpty }).map((_, i) => (
                    <span key={`cpad-${i}`} className="vop-cell vop-carry-cell-empty" />
                  ))}
                  {carry.cells.map((cell: VopCellItem) => {
                    if (cell.kind === 'digit') {
                      return <span key={cell.id} className="vop-cell vop-carry-cell-digit">{cell.value}</span>;
                    }
                    if (cell.kind === 'label') {
                      return <span key={cell.id} className="vop-cell vop-carry-cell-label">{cell.value}</span>;
                    }
                    if (cell.kind === 'color') {
                      return <span key={cell.id} className="vop-cell vop-carry-cell-color" style={{ color: cell.color }}>{cell.value}</span>;
                    }
                    if (cell.kind === 'bold') {
                      return <span key={cell.id} className="vop-cell vop-carry-cell-bold">{cell.value}</span>;
                    }
                    if (cell.kind === 'highlight') {
                      return <span key={cell.id} className="vop-cell vop-carry-cell-highlight"><mark>{cell.value}</mark></span>;
                    }
                    if (cell.kind === 'empty') {
                      return <span key={cell.id} className="vop-cell vop-carry-cell-empty" />;
                    }
                    const widget = widgets[cell.widgetId];
                    if (!widget) return <span key={cell.id} className="vop-cell" />;
                    const isCorrect = validationDetails?.[cell.widgetId]?.isCorrect;
                    const validationStatus = isCorrect === true ? 'correct' : isCorrect === false ? 'incorrect' : null;
                    return (
                      <span key={cell.id} className="vop-cell vop-carry-cell-input">
                        <WidgetRenderer
                          widget={widget}
                          widgetId={cell.widgetId}
                          value={values[cell.widgetId] || ''}
                          isDropped={droppedInputs.has(cell.widgetId)}
                          validationStatus={validationStatus}
                          onChange={onChange}
                          onInputFocus={onInputFocus}
                        />
                      </span>
                    );
                  })}
                </div>
              );
            }
            const row = child as VopRowItem;
            const showOperator = row.op !== undefined || idx === operatorRowIdx;
            const displayOp = row.op ?? vop.op;
            // Right-align: pad empty leading cells so last cell hits rightmost column
            const leadingEmpty = vop.cols - row.cells.length;
            return (
              <div key={row.id} className="vop-row">
                <span className="vop-op-cell">
                  {showOperator ? <span className="operator vop-op">{displayOp}</span> : null}
                </span>
                {Array.from({ length: leadingEmpty }).map((_, i) => (
                  <span key={`pad-${i}`} className="vop-cell vop-cell-empty" />
                ))}
                {row.cells.map((cell: VopCellItem) => {
                  if (cell.kind === 'digit') {
                    return (
                      <span key={cell.id} className="vop-cell vop-cell-digit">
                        {cell.value}
                      </span>
                    );
                  }
                  if (cell.kind === 'label') {
                    return (
                      <span key={cell.id} className="vop-cell vop-cell-label">
                        {cell.value}
                      </span>
                    );
                  }
                  if (cell.kind === 'color') {
                    return (
                      <span key={cell.id} className="vop-cell vop-cell-color" style={{ color: cell.color }}>
                        {cell.value}
                      </span>
                    );
                  }
                  if (cell.kind === 'bold') {
                    return (
                      <span key={cell.id} className="vop-cell vop-cell-bold">
                        {cell.value}
                      </span>
                    );
                  }
                  if (cell.kind === 'highlight') {
                    return (
                      <span key={cell.id} className="vop-cell vop-cell-highlight">
                        <mark>{cell.value}</mark>
                      </span>
                    );
                  }
                  if (cell.kind === 'empty') {
                    return <span key={cell.id} className="vop-cell vop-cell-empty" />;
                  }
                  const widget = widgets[cell.widgetId];
                  if (!widget) return <span key={cell.id} className="vop-cell" />;
                  const isCorrect = validationDetails?.[cell.widgetId]?.isCorrect;
                  const validationStatus =
                    isCorrect === true ? 'correct' : isCorrect === false ? 'incorrect' : null;
                  return (
                    <span key={cell.id} className="vop-cell vop-cell-input">
                      <WidgetRenderer
                        widget={widget}
                        widgetId={cell.widgetId}
                        value={values[cell.widgetId] || ''}
                        isDropped={droppedInputs.has(cell.widgetId)}
                        validationStatus={validationStatus}
                        onChange={onChange}
                        onInputFocus={onInputFocus}
                      />
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }

    case JSON_TYPES.CLOCK: {
      const clockItem = item as ClockItem;
      const widgetName = clockItem.mode === 'interactive' ? clockItem.name : undefined;
      const validationStatus = widgetName
        ? toValidationStatus(validationDetails?.[widgetName]?.isCorrect)
        : null;
      const rawValue = widgetName ? values[widgetName] : undefined;
      const value = typeof rawValue === 'string' ? rawValue : undefined;
      return (
        <ClockRenderer
          item={clockItem}
          value={value}
          validationStatus={validationStatus}
          onChange={onChange}
        />
      );
    }

    default:
      return null;
  }
};
